import * as React from 'react';
import styled, { css } from 'styled-components';

import { createUIContext } from '../../internals/createContext';
import { composeRefs } from '../../internals/composeRefs';
import { useLayerZIndex } from '../../internals/LayerManager';
import { Presence } from '../../internals/Presence';
import { Portal } from '../../internals/Portal';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Slot } from '../../internals/Slot';
import { useControllable } from '../../internals/useControllable';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useFocusTrap } from '../../internals/useFocusTrap';
import { useId } from '../../internals/useId';
import { useScrollLock } from '../../internals/useScrollLock';
import { useTheme } from '../../theme/ThemeProvider';
import { Spinner } from '../../primitives/Spinner/Spinner';
import { confirm } from '../../integrations/confirm';
import { breakpoints } from '../../tokens/breakpoints';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';

/* ─────────────────────────── Types ────────────────────────────────────── */

export type ModalSize =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | 'xxl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'full';

/* ─────────────────────────── Context ──────────────────────────────────── */

export interface ModalRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Fires after the content panel's exit transition completes. */
  onExitComplete?: () => void;
  children: React.ReactNode;
}

interface ModalContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExitComplete?: () => void;
  titleId: string;
  descriptionId: string;
}

const [ModalProvider, useModalContext] = createUIContext<ModalContextValue>('Modal');

export { useModalContext };

/* ─────────────────────────── Root ──────────────────────────────────────── */

const ModalRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  onExitComplete,
  children,
}: ModalRootProps) => {
  const [isOpen, setOpen] = useControllable<boolean>({
    value: open,
    defaultValue: defaultOpen ?? false,
    onChange: onOpenChange,
  });
  const titleId = useId('modal-title');
  const descriptionId = useId('modal-description');

  const value = React.useMemo(
    () => ({ open: isOpen, onOpenChange: setOpen, onExitComplete, titleId, descriptionId }),
    [isOpen, setOpen, onExitComplete, titleId, descriptionId]
  );

  return <ModalProvider value={value}>{children}</ModalProvider>;
};

ModalRoot.displayName = 'Modal.Root';

/* ─────────────────────────── Trigger ──────────────────────────────────── */

export interface ModalTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  semanticId?: string;
}

const ModalTrigger = React.forwardRef<HTMLButtonElement, ModalTriggerProps>(function ModalTrigger(
  { asChild = false, semanticId, onClick, type = 'button', children, ...rest },
  ref
) {
  const { open, onOpenChange } = useModalContext();
  const semanticProps = getSemanticAttributes(semanticId);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) onOpenChange(true);
  };

  if (asChild) {
    return (
      <Slot
        ref={ref as React.Ref<HTMLElement>}
        data-state={open ? 'open' : 'closed'}
        onClick={handleClick as React.MouseEventHandler<HTMLElement>}
        {...semanticProps}
        {...(rest as unknown as React.ComponentProps<typeof Slot>)}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      data-state={open ? 'open' : 'closed'}
      onClick={handleClick}
      {...semanticProps}
      {...rest}>
      {children}
    </button>
  );
});

ModalTrigger.displayName = 'Modal.Trigger';

/* ─────────────────────────── Portal ──────────────────────────────────── */

export interface ModalPortalProps {
  children: React.ReactNode;
}

/**
 * Stacking root positioned below the navbar. Uses `--ui-navbar-offset` (set by
 * DS Navbar) so the overlay and panel never cover the top chrome.
 * `isolation: isolate` scopes internal z-indexes.
 * Re-injects theme CSS variables so token-based styles work outside ThemeProvider DOM.
 * z-index is applied dynamically via LayerManager to support modal-over-modal stacking.
 */
const ModalPortalStack = styled.div`
  position: fixed;
  top: var(--ui-navbar-offset, 67px);
  left: 0;
  right: 0;
  bottom: 0;
  isolation: isolate;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const ModalPortal = ({ children }: ModalPortalProps) => {
  const { theme } = useTheme();
  const layerZ = useLayerZIndex('modal');
  return (
    <Portal>
      <ModalPortalStack
        data-wl-modal-stack=''
        style={{ ...(theme.cssVars as React.CSSProperties), zIndex: layerZ }}>
        {children}
      </ModalPortalStack>
    </Portal>
  );
};

ModalPortal.displayName = 'Modal.Portal';

/* ─────────────────────────── Overlay ─────────────────────────────────── */

const StyledOverlay = styled.div`
  position: fixed;
  top: var(--ui-navbar-offset, 67px);
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  background: var(--ui-overlay, rgba(0, 0, 0, 0.85));
  opacity: 0;
  transition: opacity 0.35s ease;

  &[data-state='open'] {
    opacity: 1;
  }
`;

export interface ModalOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  /** When true, clicking the dark backdrop area closes the modal. */
  closeOnOverlayClick?: boolean;
  semanticId?: string;
}

const ModalOverlay = React.forwardRef<HTMLDivElement, ModalOverlayProps>(function ModalOverlay(
  { closeOnOverlayClick = false, semanticId, onClick, children, ...rest },
  ref
) {
  const { open, onOpenChange } = useModalContext();
  const semanticProps = getSemanticAttributes(semanticId);

  useScrollLock(open);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented && closeOnOverlayClick) {
      onOpenChange(false);
    }
  };

  return (
    <>
      <Presence present={open} animateEnter>
        <StyledOverlay ref={ref} onClick={handleClick} {...semanticProps} {...rest} />
      </Presence>
      {children}
    </>
  );
});

ModalOverlay.displayName = 'Modal.Overlay';

/* ─────────────────────────── Content (the panel) ─────────────────────── */

/**
 * Preferred panel widths per size token.
 *
 * Always use `min(preferred, 100% - margins)` so large tokens (4xl–7xl)
 * never overflow the viewport. The panel's base `max-width` is a safety
 * net; SIZE_MAP entries also clamp so media-query widths stay honest.
 *
 * Margin budget: 16px × 2 = 32px (matches StyledPanel margin).
 */
const PANEL_MAX = `calc(100% - ${rem(32)})`;

const SIZE_MAP: Record<ModalSize, ReturnType<typeof css>> = {
  sm: css`
    width: min(400px, ${PANEL_MAX});
  `,
  md: css`
    width: min(500px, ${PANEL_MAX});
  `,
  lg: css`
    width: min(700px, ${PANEL_MAX});
  `,
  xl: css`
    width: min(800px, ${PANEL_MAX});
  `,
  xxl: css`
    width: min(900px, ${PANEL_MAX});
  `,
  '3xl': css`
    width: min(1114px, ${PANEL_MAX});
  `,
  '4xl': css`
    width: min(1240px, ${PANEL_MAX});
  `,
  '5xl': css`
    width: min(1440px, ${PANEL_MAX});
  `,
  '6xl': css`
    width: min(1680px, ${PANEL_MAX});
  `,
  '7xl': css`
    width: min(1920px, ${PANEL_MAX});
  `,
  /**
   * Fill the portal area (viewport minus 16px margins).
   * Do NOT use 90vw — that leaves a large left gutter on a right-anchored panel.
   */
  full: css`
    width: ${PANEL_MAX} !important;
    max-width: ${PANEL_MAX} !important;
  `,
};

const StyledPanel = styled.div<{ $size: ModalSize }>`
  position: fixed;
  top: var(--ui-navbar-offset, 67px);
  right: 0;
  bottom: 0;
  z-index: 1;
  box-sizing: border-box;
  background: var(--ui-surface, ${palette.White});
  color: var(--ui-text, inherit);
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  width: ${PANEL_MAX};
  max-width: ${PANEL_MAX};
  height: calc(100vh - var(--ui-navbar-offset, 67px) - ${rem(32)});
  display: flex;
  flex-direction: column;
  border-radius: var(--ui-radius-lg, ${rem(10)});
  margin: ${rem(16)};
  overflow: auto;
  -webkit-font-smoothing: antialiased;

  transform: translateX(100%);
  transition: transform 0.35s ease;

  &[data-state='open'] {
    transform: translateX(0);
  }

  ${({ $size }) => SIZE_MAP[$size] ?? ''}
`;

export interface ModalContentProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: ModalSize;
  semanticId?: string;
}

const ModalContent = React.forwardRef<HTMLDivElement, ModalContentProps>(function ModalContent(
  { size = 'md', semanticId, children, ...rest },
  ref
) {
  const { open, onOpenChange, onExitComplete, titleId, descriptionId } = useModalContext();
  const focusTrapRef = useFocusTrap(open);

  const close = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  useEscapeKey(close, open);

  const composedRef = composeRefs(ref, focusTrapRef);
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <Presence present={open} animateEnter onExitComplete={onExitComplete}>
      <StyledPanel
        ref={composedRef}
        $size={size}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        {...semanticProps}
        {...rest}>
        {children}
      </StyledPanel>
    </Presence>
  );
});

ModalContent.displayName = 'Modal.Content';

/* ─────────────────────────── Header ──────────────────────────────────── */

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: ${rem(1)} solid var(--ui-border-muted, ${palette.CulturedWhite});
  padding: ${rem(24)} ${rem(32)} ${rem(21)} ${rem(32)};
  align-items: flex-start;

  @media (max-width: ${breakpoints.sm}px) {
    padding: ${rem(16)} ${rem(16)} ${rem(14)} ${rem(16)};
  }
`;

export interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const ModalHeader = React.forwardRef<HTMLDivElement, ModalHeaderProps>(function ModalHeader(
  { semanticId, children, ...rest },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);
  return (
    <StyledHeader ref={ref} {...semanticProps} {...rest}>
      {children}
    </StyledHeader>
  );
});

ModalHeader.displayName = 'Modal.Header';

/* ─────────────────────────── HeaderText ───────────────────────────────── */

export type HeaderTextLayout = 'vertical' | 'horizontal';

const StyledHeaderText = styled.div<{ $layout: HeaderTextLayout }>`
  display: flex;
  flex-direction: ${({ $layout }) => ($layout === 'horizontal' ? 'row' : 'column')};
  align-items: ${({ $layout }) => ($layout === 'horizontal' ? 'center' : 'stretch')};
  gap: ${({ $layout }) => ($layout === 'horizontal' ? rem(8) : rem(4))};
  min-width: 0;
  flex-wrap: ${({ $layout }) => ($layout === 'horizontal' ? 'wrap' : 'nowrap')};

  @media (max-width: ${breakpoints.sm}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${rem(4)};
  }
`;

export interface ModalHeaderTextProps extends React.HTMLAttributes<HTMLDivElement> {
  layout?: HeaderTextLayout;
}

const ModalHeaderText = React.forwardRef<HTMLDivElement, ModalHeaderTextProps>(
  function ModalHeaderText({ layout = 'vertical', children, ...rest }, ref) {
    return (
      <StyledHeaderText ref={ref} $layout={layout} {...rest}>
        {children}
      </StyledHeaderText>
    );
  }
);

ModalHeaderText.displayName = 'Modal.HeaderText';

/* ─────────────────────────── Title ───────────────────────────────────── */

export interface ModalTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  semanticId?: string;
}

const StyledTitle = styled.h4`
  font-weight: 500;
  font-size: ${rem(22)};
  line-height: ${rem(25)};
  margin: 0;
  color: var(--ui-text, ${palette.Black});
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;

  @media (max-width: ${breakpoints.sm}px) {
    font-size: ${rem(18)};
    line-height: ${rem(22)};
    white-space: normal;
    word-break: break-word;
  }
`;

const ModalTitle = React.forwardRef<HTMLHeadingElement, ModalTitleProps>(function ModalTitle(
  { semanticId, ...rest },
  ref
) {
  const { titleId } = useModalContext();
  const semanticProps = getSemanticAttributes(semanticId);
  return <StyledTitle ref={ref} {...semanticProps} {...rest} id={titleId} />;
});

ModalTitle.displayName = 'Modal.Title';

/* ─────────────────────────── Subtitle ────────────────────────────────── */

export interface ModalSubtitleProps extends React.HTMLAttributes<HTMLParagraphElement> {
  semanticId?: string;
}

const StyledSubtitle = styled.p`
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  font-weight: 400;
  margin: 0;
  color: var(--ui-text-subtle, ${palette.Gray});
`;

const ModalSubtitle = React.forwardRef<HTMLParagraphElement, ModalSubtitleProps>(
  function ModalSubtitle({ semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledSubtitle ref={ref} {...semanticProps} {...rest} />;
  }
);

ModalSubtitle.displayName = 'Modal.Subtitle';

/* ─────────────────────────── Description ─────────────────────────────── */

export interface ModalDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  semanticId?: string;
}

const ModalDescription = React.forwardRef<HTMLParagraphElement, ModalDescriptionProps>(
  function ModalDescription({ semanticId, ...rest }, ref) {
    const { descriptionId } = useModalContext();
    const semanticProps = getSemanticAttributes(semanticId);
    return <p ref={ref} {...semanticProps} {...rest} id={descriptionId} />;
  }
);

ModalDescription.displayName = 'Modal.Description';

/* ─────────────────────────── Close ───────────────────────────────────── */

export interface ModalCloseProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
  /** When true, clicking Close shows a confirm dialog before closing. */
  dirty?: boolean;
  /** Confirm dialog message when dirty. */
  confirmMessage?: string;
}

const StyledClose = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${rem(2)};
  cursor: pointer;
  flex-shrink: 0;

  span {
    transition: all 0.5s 0s ease;
  }

  &:hover span {
    color: var(--ui-danger, ${palette.Red});
  }

  .icon-color {
    color: var(--ui-text-subtle, ${palette.Silver});
  }
`;

const StyledCloseLabel = styled.span`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
`;

const DEFAULT_CLOSE_CONFIRM_MESSAGE =
  'Your changes will not be saved. Are you sure you want to close?';

const ModalClose = React.forwardRef<HTMLDivElement, ModalCloseProps>(function ModalClose(
  { semanticId, dirty, confirmMessage, onClick, children, ...rest },
  ref
) {
  const { onOpenChange } = useModalContext();
  const semanticProps = getSemanticAttributes(semanticId);

  const handleClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;

    if (dirty) {
      const { confirmed } = await confirm({
        title: confirmMessage ?? DEFAULT_CLOSE_CONFIRM_MESSAGE,
      });
      if (!confirmed) return;
    }

    onOpenChange(false);
  };

  return (
    <StyledClose ref={ref} onClick={handleClick} {...semanticProps} {...rest}>
      {children ?? (
        <>
          <span className='material-symbols-outlined icon-color'>cancel</span>
          <StyledCloseLabel>Close</StyledCloseLabel>
        </>
      )}
    </StyledClose>
  );
});

ModalClose.displayName = 'Modal.Close';

/* ─────────────────────────── Body ────────────────────────────────────── */

const StyledBody = styled.div`
  padding: ${rem(24)} ${rem(32)};
  overflow-y: auto;
  flex: 1;

  ::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: var(--ui-radius-md, 7px);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--ui-text-subtle, ${palette.SpanishGray});
    border-radius: var(--ui-radius-md, 7px);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const StyledLoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

export interface ModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Shows a centered spinner instead of children. */
  loading?: boolean;
  semanticId?: string;
}

const ModalBody = React.forwardRef<HTMLDivElement, ModalBodyProps>(function ModalBody(
  { loading = false, semanticId, children, ...rest },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <StyledBody
      ref={ref}
      {...semanticProps}
      {...rest}
      style={loading ? { display: 'flex', flexDirection: 'column', ...rest.style } : rest.style}>
      {loading && (
        <StyledLoaderContainer>
          <Spinner size='lg' />
        </StyledLoaderContainer>
      )}
      <div style={loading ? { position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0 } : undefined}>
        {children}
      </div>
    </StyledBody>
  );
});

ModalBody.displayName = 'Modal.Body';

/* ─────────────────────────── Footer ──────────────────────────────────── */

const StyledFooter = styled.div`
  box-sizing: border-box;
  width: 100%;
  border-top: ${rem(1)} solid var(--ui-border-muted, ${palette.CulturedWhite});
  background-color: var(--ui-surface, ${palette.White});
  padding: ${rem(20)} ${rem(32)};
  border-bottom-right-radius: var(--ui-radius-lg, ${rem(10)});
  border-bottom-left-radius: var(--ui-radius-lg, ${rem(10)});
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  margin-top: auto;
`;

export interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const ModalFooter = React.forwardRef<HTMLDivElement, ModalFooterProps>(function ModalFooter(
  { semanticId, children, ...rest },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);
  return (
    <StyledFooter ref={ref} {...semanticProps} {...rest}>
      {children}
    </StyledFooter>
  );
});

ModalFooter.displayName = 'Modal.Footer';

/* ─────────────────────────── Compound export ──────────────────────────── */

import { ModalCancel, ModalAction } from './ModalFooterParts';

export const Modal = {
  Root: ModalRoot,
  Trigger: ModalTrigger,
  Portal: ModalPortal,
  Overlay: ModalOverlay,
  Content: ModalContent,
  Header: ModalHeader,
  HeaderText: ModalHeaderText,
  Body: ModalBody,
  Footer: ModalFooter,
  Close: ModalClose,
  Title: ModalTitle,
  Subtitle: ModalSubtitle,
  Description: ModalDescription,
  Cancel: ModalCancel,
  Action: ModalAction,
};
